/**
 * eReg App Component
 *
 * Electronic regulatory document management with e-signatures.
 * Integrated into SiteOperations.io - uses shared authentication.
 * Note: EregProvider is now in App.tsx to persist state across navigation.
 *
 * Phase 1 shell (friction audit §8 items 8–9): ONE ProtectedShell (session +
 * entitlement gate rendering the persistent Layout) wraps every nested route
 * via <Outlet/>, so page hops no longer remount the shell or re-run the
 * /api/my-tools check behind a full-screen spinner. Study pages live at
 * URL-canonical /ereg/studies/:protocolId/* (StudyScope), site documents at
 * /ereg/site/* (SiteScope), and the old context-driven paths redirect
 * (LegacySectionRoute).
 */

import { useState, useEffect } from 'react';
import { Routes, Route, Navigate, Outlet, useNavigate } from 'react-router-dom';
import { useEreg } from './contexts';
import { Layout } from './components/layout';
import { Dashboard, Protocols, Folders, Upload, Settings, Intake, NetworkDocuments, AuditTrail } from './pages';
import { SignatureQueue } from './pages/SignatureQueue';
import { DelegationLogPage } from './pages/DelegationLog';
import { StudyCreate } from './pages/StudyCreate';
import { StudyScope, StudyIndexRedirect, SiteScope, LegacySectionRoute } from './pages/StudyScope';
import { useAuth } from '../contexts/AuthContext';
import { ToastProvider } from '../ui';
import api from '../services/api';

// Session + entitlement gate for the whole module — mounted ONCE around the
// nested routes. Uses shared auth from the main app.
function ProtectedShell() {
  const { session, user: authUser, currentSite } = useAuth();
  const { loading } = useEreg();
  const navigate = useNavigate();
  const [entitlementChecked, setEntitlementChecked] = useState(false);
  const [hasEntitlement, setHasEntitlement] = useState(false);
  const [checkingEntitlement, setCheckingEntitlement] = useState(true);

  // Check entitlement on module entry or when the site changes
  useEffect(() => {
    const checkEntitlement = async () => {
      // Super admins always have access
      if (authUser?.role === 'super_admin') {
        setHasEntitlement(true);
        setEntitlementChecked(true);
        setCheckingEntitlement(false);
        return;
      }

      if (!currentSite) {
        // No site selected = no access to tools
        setHasEntitlement(false);
        setEntitlementChecked(true);
        setCheckingEntitlement(false);
        return;
      }

      try {
        const response = await api.get('/api/my-tools');
        const tools = response.data || [];
        const eregTool = tools.find((t: any) => t.tool_id === 'ereg');
        setHasEntitlement(eregTool?.is_available === true);
        setEntitlementChecked(true);
      } catch (error) {
        console.error('Error checking eReg entitlement:', error);
        setHasEntitlement(false);
        setEntitlementChecked(true);
      } finally {
        setCheckingEntitlement(false);
      }
    };

    if (session && !loading) {
      checkEntitlement();
    }
  }, [session, authUser?.role, currentSite, loading]);

  if (loading || checkingEntitlement) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 dark:border-primary-400"></div>
      </div>
    );
  }

  // If no session, redirect to main login
  if (!session) {
    return <Navigate to="/login" replace />;
  }

  // Check entitlement (super admins bypass this check)
  if (entitlementChecked && !hasEntitlement && authUser?.role !== 'super_admin') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center p-8 max-w-md">
          <div className="text-6xl mb-4">🔒</div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">Access Denied</h1>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            You do not have access to eReg for your current site.
            Please contact your administrator if you believe this is an error.
          </p>
          <button
            onClick={() => navigate('/')}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors duration-150"
          >
            Return to Tool Hub
          </button>
        </div>
      </div>
    );
  }

  // Layout renders the persistent Header/Sidebar shell around an <Outlet/>
  return <Layout />;
}

// Admin-only branch (Intake / Manage Studies / Audit Trail) — same silent
// dashboard redirect as before.
function RequireAdmin() {
  const { isAdmin } = useEreg();
  if (!isAdmin) {
    return <Navigate to="/ereg/dashboard" replace />;
  }
  return <Outlet />;
}

// eReg Routes
function EregRoutes() {
  return (
    <Routes>
      {/* Default route goes to dashboard */}
      <Route index element={<Navigate to="/ereg/dashboard" replace />} />

      <Route element={<ProtectedShell />}>
        <Route path="dashboard" element={<Dashboard />} />

        {/* URL-canonical study scope (§3.6 deep links + Phase 1 item 8):
            refresh/back/shared links keep the study */}
        <Route path="studies/new" element={<StudyCreate />} />
        <Route path="studies/:protocolId" element={<StudyScope />}>
          <Route index element={<StudyIndexRedirect />} />
          <Route path="folders" element={<Folders />} />
          <Route path="upload" element={<Upload />} />
          <Route path="signatures" element={<SignatureQueue />} />
          <Route path="delegation" element={<DelegationLogPage />} />
        </Route>

        {/* Site-documents zone (CVs, licenses, certifications) */}
        <Route path="site" element={<SiteScope />}>
          <Route index element={<Navigate to="folders" replace />} />
          <Route path="folders" element={<Folders />} />
          <Route path="upload" element={<Upload />} />
        </Route>

        {/* Legacy context-driven URLs → canonical (query string rides along);
            "documents" was always a plain alias for folders */}
        <Route path="folders" element={<LegacySectionRoute page="folders" />} />
        <Route path="documents" element={<LegacySectionRoute page="folders" />} />
        <Route path="upload" element={<LegacySectionRoute page="upload" />} />
        <Route path="signatures" element={<LegacySectionRoute page="signatures" />} />
        <Route path="delegation" element={<LegacySectionRoute page="delegation" />} />

        {/* Network-wide reference documents (Phase 5) — every eReg user */}
        <Route path="network" element={<NetworkDocuments />} />

        {/* Settings */}
        <Route path="settings" element={<Settings />} />

        {/* Admin-only pages */}
        <Route element={<RequireAdmin />}>
          <Route path="intake" element={<Intake />} />
          <Route path="protocols" element={<Protocols />} />
          <Route path="audit" element={<AuditTrail />} />
        </Route>
      </Route>

      {/* Catch all - redirect to dashboard */}
      <Route path="*" element={<Navigate to="/ereg/dashboard" replace />} />
    </Routes>
  );
}

// Main eReg App
// Note: Provider is now in App.tsx to persist state across navigation
export function EregApp() {
  return (
    <ToastProvider>
      <EregRoutes />
    </ToastProvider>
  );
}

export default EregApp;
